import { isBrowser } from './helpers.js';
import { getTransformedPreviewBranch, isLocal, isPreview } from './metadata.js';

/**
 * Get the base domain from the current host
 * Examples:
 *   app.magicschool.ai -> magicschool.ai
 *   app.qa.eu-shared.magicschooldev.com -> magicschooldev.com
 *   eu.magicschool.ai -> magicschool.ai
 * @param {string} host
 * @returns {string}
 */
const getBaseDomain = (host) => {
  const parts = host.split('.');

  // For .vercel.app domains, return the full domain
  if (host.endsWith('.vercel.app')) {
    return host;
  }

  // For custom domains, return last 2 parts (domain.tld)
  // This handles: magicschool.ai, magicschooldev.com, etc.
  // NOTE: This assumes two-part TLDs (.ai, .com) and would not work correctly
  // with multi-part TLDs like .co.uk. If support for such TLDs is needed in the
  // future, consider using a library like psl (Public Suffix List).
  if (parts.length >= 2) {
    return parts.slice(-2).join('.');
  }

  return host;
};

/**
 * Get the environment prefix from the current host
 * Examples:
 *   app.qa.eu-shared.magicschooldev.com -> qa.eu-shared
 *   app.staging.magicschool.ai -> staging
 *   app.magicschool.ai -> (empty string)
 * @param {string} host
 * @returns {string}
 */
const getEnvironmentPrefix = (host) => {
  const baseDomain = getBaseDomain(host);

  // Remove the subdomain (app/student/login) and base domain
  const withoutSubdomain = host.replace(/^(app|student|login|teacher)\./, '');
  const envPrefix = withoutSubdomain.replace(`.${baseDomain}`, '');

  // If envPrefix equals baseDomain, there's no environment prefix
  return envPrefix === baseDomain ? '' : envPrefix;
};

/**
 * Resolve the current host from the given argument, browser, or environment variables.
 * @param {string | undefined} currentHost
 * @returns {string | undefined}
 */
const resolveCurrentHost = (currentHost) => {
  if (currentHost) return currentHost;
  if (isBrowser()) return window.location.host;
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  if (siteUrl) return siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return process.env.NEXT_PUBLIC_VERCEL_URL || process.env.VERCEL_URL;
};

/**
 * Build a full URL from a subdomain and resolved host.
 * @param {string} subdomain
 * @param {string} host
 * @returns {string}
 */
const buildUrlFromHost = (subdomain, host) => {
  const baseDomain = getBaseDomain(host);
  const envPrefix = getEnvironmentPrefix(host);
  return envPrefix ? `https://${subdomain}.${envPrefix}.${baseDomain}/` : `https://${subdomain}.${baseDomain}/`;
};

/**
 * Build a URL for a specific subdomain in the current environment
 * @param {string} subdomain - 'app', 'student', 'login', etc.
 * @param {string | undefined} currentHost - Current host (optional, will detect from browser)
 * @returns {string}
 */
const buildUrlForSubdomain = (subdomain, currentHost = undefined) => {
  // If explicit environment variables are set, use them
  // Note: the teacher app uses 'app' as its subdomain key, not 'teacher'
  const envVarMap = {
    app: process.env.NEXT_PUBLIC_TEACHER_URL,
    student: process.env.NEXT_PUBLIC_STUDENT_URL,
    login: process.env.NEXT_PUBLIC_LOGIN_URL,
  };

  if (envVarMap[subdomain]) {
    return envVarMap[subdomain];
  }

  // Local development
  if (isLocal()) {
    const localMap = {
      app: 'http://localhost:3000/',
      student: 'http://student.localhost:3000/',
      login: 'http://login.localhost:3000/',
    };
    return localMap[subdomain] || 'http://localhost:3000/';
  }

  // Preview deployments (Vercel branch deployments)
  if (isPreview()) {
    const branch = getTransformedPreviewBranch();
    const previewMap = {
      app: `https://teacher-${branch}-magicschoolai.vercel.app/`,
      student: `https://student-${branch}-magicschoolai.vercel.app/`,
      login: `https://login-${branch}-magicschoolai.vercel.app/`,
    };
    return previewMap[subdomain] || `https://teacher-${branch}-magicschoolai.vercel.app/`;
  }

  // Production/Custom domains - derive from current host
  const host = resolveCurrentHost(currentHost);

  if (!host) {
    const fallbackMap = {
      app: 'https://app.magicschool.ai/',
      student: 'https://student.magicschool.ai/',
      login: 'https://login.magicschool.ai/',
    };
    return fallbackMap[subdomain] || 'https://app.magicschool.ai/';
  }

  return buildUrlFromHost(subdomain, host);
};

/**
 * Get the site URL.
 * @returns {string}
 */
export const getUrl = () => {
  if (isBrowser()) {
    return window.location.origin;
  }

  let url =
    process.env.NEXT_PUBLIC_SITE_URL || // Set this to your site URL in production env.
    process.env.NEXT_PUBLIC_VERCEL_URL || // Automatically set by Vercel.
    'http://localhost:3000/';

  // Make sure to include `https://` when not localhost.
  url = url.includes('http') ? url : `https://${url}`;

  // Make sure to include a trailing `/`.
  url = url.charAt(url.length - 1) === '/' ? url : `${url}/`;

  return url;
};

/**
 * Get the site host.
 * @param {string | undefined} url
 * @returns {string}
 */
export const getHost = (url = undefined) => {
  url = url ?? getUrl();
  try {
    return new URL(url).host;
  } catch (err) {
    console.error(err);
    throw new Error(`Invalid URL: ${url}`);
  }
};

/**
 * Get the URL for the student app
 * @returns {string}
 */
export const getStudentUrl = () => {
  return buildUrlForSubdomain('student');
};

/**
 * Get the URL for the teacher app
 * @returns {string}
 */
export const getTeacherUrl = () => {
  return buildUrlForSubdomain('app');
};

/**
 * Get the URL for the login app
 * @returns {string}
 */
export const getLoginUrl = () => {
  return buildUrlForSubdomain('login');
};

/**
 * Is the URL a student URL?
 * @param {string} url
 * @returns {boolean}
 */
export const isStudentUrl = (url) => {
  const host = getHost(url);
  return isStudentHost(host);
};

/**
 * Is the URL a teacher URL?
 * @param {string} url
 * @returns {boolean}
 */
export const isTeacherUrl = (url) => {
  const host = getHost(url);
  return isTeacherHost(host);
};

/**
 * Is the host a student host?
 * Pattern-based detection that works regardless of environment variables.
 * @param {string} host
 */
export const isStudentHost = (host) => {
  // Check for student patterns: student.*, student-*
  return host.startsWith('student.') || host.startsWith('student-');
};

/**
 * Is the host a teacher host?
 * Pattern-based detection that works regardless of environment variables.
 * Defaults to true for any host that is not explicitly a student or login host.
 * @param {string} host
 */
export const isTeacherHost = (host) => {
  // Explicitly exclude student and login hosts
  if (host.startsWith('student.') || host.startsWith('student-') || host.startsWith('login.') || host.startsWith('login-')) {
    return false;
  }

  // Everything else is considered a teacher host (including app.*, teacher.*, localhost, QA domains, etc.)
  return true;
};

/**
 * Is the host a login host?
 * Pattern-based detection that works regardless of environment variables.
 * @param {string} host
 */
export const isLoginHost = (host) => {
  // Check for login patterns: login.*, login-*
  return host.startsWith('login.') || host.startsWith('login-');
};
